import { useState, useRef, useEffect } from 'react';

// Optimized image component with lazy loading

interface OptimizedImageProps {
  src: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  sizes?: string;
  priority?: boolean;
  onClick?: () => void;
  onLoad?: () => void;
  onError?: () => void;
}

export const OptimizedImage = ({ 
  src, 
  alt, 
  className = "", 
  width = 800,
  height = 600,
  sizes = "(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw",
  priority = false,
  onClick,
  onLoad,
  onError
}: OptimizedImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgContainerRef = useRef<HTMLDivElement>(null);
  const imgElRef = useRef<HTMLImageElement>(null);

  // Ensure we mark as loaded if the image comes from cache
  useEffect(() => {
    const img = imgElRef.current;
    if (!img) return;
    if (img.complete && img.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, [src]);

  const handleLoad = () => {
    setIsLoaded(true);
    console.log('[OptimizedImage] loaded', { src, naturalWidth: imgElRef.current?.naturalWidth, naturalHeight: imgElRef.current?.naturalHeight });
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    setIsLoaded(true);
    console.error('[OptimizedImage] error', { src });
    onError?.();
  };

  return (
    <div 
      ref={imgContainerRef}
      className={`relative overflow-hidden ${className}`}
    >
      <img
        ref={imgElRef}
        src={hasError ? '/placeholder.svg' : src}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        className={`w-full h-full object-cover ${onClick ? 'cursor-pointer' : ''}`}
        onClick={onClick}
        onLoad={handleLoad}
        onError={handleError}
      />
      
    </div>
  );
};